import { memo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Scenario } from 'models/response/scenario.interface';
import ScenarioDatePicker from 'pages/PlanPageForecast/Plan/Filters/ScenarioDatePicker';
import { CreateScenarios, FetchScenarios } from 'redux/BuilderModeSlice';
import { getAllScenarios, getBaseScenario, getPlannerModel } from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { BUILDER_MODE_V1_LAYOUT, BUILDER_MODE_V1_TOKENS } from '../constants';
import BuilderModeSvgIcon from '../icons/BuilderModeSvgIcon';

const validationSchema = Yup.object({
  scenarioName: Yup.string().required('Scenario name is required'),
  scenarioDescription: Yup.string().max(120, 'Description is too long'),
});

const fieldLabelStyles = {
  fontSize: '14px',
  fontWeight: '500',
  color: BUILDER_MODE_V1_TOKENS.textDark,
  fontFamily: BUILDER_MODE_V1_LAYOUT.toolbarFontFamily,
  mb: '8px',
};

const fieldInputStyles = {
  h: '44px',
  px: '12px',
  borderRadius: BUILDER_MODE_V1_LAYOUT.actionButtonRadius,
  border: '1px solid',
  borderColor: BUILDER_MODE_V1_TOKENS.border,
  fontSize: '14px',
  fontWeight: '500',
  color: BUILDER_MODE_V1_TOKENS.textDark,
  fontFamily: BUILDER_MODE_V1_LAYOUT.toolbarFontFamily,
  _placeholder: { color: BUILDER_MODE_V1_TOKENS.placeholder, fontWeight: '400' },
  _focus: {
    borderColor: BUILDER_MODE_V1_TOKENS.primary,
    boxShadow: 'none',
  },
};

interface AddScenarioFormV1Properties {
  onCancel: () => void;
}

const AddScenarioFormV1 = memo(({ onCancel }: AddScenarioFormV1Properties) => {
  const dispatch = useDispatch<AppDispatch>();
  const { modelId } = useParams();
  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const plannerModel = useSelector(getPlannerModel);
  const defaultStartDate = plannerModel?.time_properties?.starting_period ?? '';
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(
    baseScenario?.id ?? null,
  );

  const selectedScenario = scenarios?.find(
    (scenario: Scenario) => scenario.id === selectedScenarioId,
  );

  useEffect(() => {
    setSelectedScenarioId(baseScenario?.id ?? null);
  }, [baseScenario?.id]);

  const handleSubmit = async (values: {
    scenarioName: string;
    scenarioDescription: string;
    startDate: string;
  }) => {
    if (!modelId) return;

    await dispatch(
      CreateScenarios({
        modelId,
        data: {
          name: values.scenarioName,
          description: values.scenarioDescription,
          duplicate_id: selectedScenarioId,
          start_date: values.startDate,
        },
        toaster: {
          successMessage: 'Successfully added scenario',
          errorMessage: 'A scenario with this name already exists',
        },
      }),
    );
    await dispatch(FetchScenarios(Number(modelId)));
    onCancel();
  };

  return (
    <Box w='100%'>
      <Text
        textAlign='center'
        fontSize='16px'
        fontWeight='600'
        color={BUILDER_MODE_V1_TOKENS.textDark}
        fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
        mb='20px'
      >
        Add New Scenario
      </Text>

      <Formik
        initialValues={{
          scenarioName: '',
          scenarioDescription: '',
          startDate: defaultStartDate,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values, handleChange, setFieldValue, resetForm, isSubmitting }) => (
          <Form>
            <FormControl mb='16px' isInvalid={Boolean(touched.scenarioName && errors.scenarioName)}>
              <FormLabel {...fieldLabelStyles}>Name</FormLabel>
              <Input
                name='scenarioName'
                placeholder='E.g. Baseline'
                value={values.scenarioName}
                onChange={handleChange}
                {...fieldInputStyles}
              />
              {touched.scenarioName && errors.scenarioName && (
                <Text fontSize='12px' color='red.500' mt={1}>
                  {errors.scenarioName}
                </Text>
              )}
            </FormControl>

            <FormControl mb='16px'>
              <Flex align='center' justify='space-between' mb='8px'>
                <FormLabel {...fieldLabelStyles} mb={0}>
                  Description
                </FormLabel>
                <Text fontSize='12px' color={BUILDER_MODE_V1_TOKENS.textSecondary}>
                  20 words max
                </Text>
              </Flex>
              <Textarea
                name='scenarioDescription'
                placeholder='Write something about this scenario...'
                value={values.scenarioDescription}
                onChange={handleChange}
                minH='88px'
                resize='none'
                px='12px'
                py='12px'
                borderRadius={BUILDER_MODE_V1_LAYOUT.actionButtonRadius}
                border='1px solid'
                borderColor={BUILDER_MODE_V1_TOKENS.border}
                fontSize='14px'
                color={BUILDER_MODE_V1_TOKENS.textDark}
                fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                _placeholder={{ color: BUILDER_MODE_V1_TOKENS.placeholder }}
                _focus={{
                  borderColor: BUILDER_MODE_V1_TOKENS.primary,
                  boxShadow: 'none',
                }}
              />
            </FormControl>

            <FormControl mb='16px'>
              <FormLabel {...fieldLabelStyles}>Duplicate Scenario</FormLabel>
              <Menu matchWidth>
                <MenuButton
                  as={Button}
                  variant='outline'
                  w='100%'
                  h='44px'
                  px='12px'
                  justifyContent='space-between'
                  fontWeight='500'
                  fontSize='14px'
                  color={BUILDER_MODE_V1_TOKENS.textDark}
                  borderColor={BUILDER_MODE_V1_TOKENS.border}
                  borderRadius={BUILDER_MODE_V1_LAYOUT.actionButtonRadius}
                  rightIcon={<BuilderModeSvgIcon name='chevronDown' width={9} height={6} />}
                  _hover={{ bg: BUILDER_MODE_V1_TOKENS.activeBg }}
                  _active={{ bg: BUILDER_MODE_V1_TOKENS.activeBg }}
                >
                  <Text noOfLines={1} textAlign='left'>
                    {selectedScenario?.name ?? 'Baseline'}
                  </Text>
                </MenuButton>
                <MenuList
                  maxH='200px'
                  overflowY='auto'
                  borderRadius={BUILDER_MODE_V1_LAYOUT.actionButtonRadius}
                  borderColor={BUILDER_MODE_V1_TOKENS.border}
                  className='scroll'
                >
                  <MenuOptionGroup type='radio' value={String(selectedScenarioId ?? '')}>
                    {scenarios?.map((scenario: Scenario) => (
                      <MenuItemOption
                        key={scenario.id}
                        value={String(scenario.id)}
                        onClick={() => setSelectedScenarioId(scenario.id)}
                      >
                        {scenario.name}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </FormControl>

            <FormControl mb='20px'>
              <FormLabel {...fieldLabelStyles}>Forecast start date</FormLabel>
              <Box
                bg={BUILDER_MODE_V1_TOKENS.activeBg}
                borderRadius={BUILDER_MODE_V1_LAYOUT.actionButtonRadius}
                px='12px'
                py='8px'
                w='fit-content'
              >
                <ScenarioDatePicker
                  clearSection={false}
                  clearDateInput={() => setFieldValue('startDate', '')}
                  selected={values.startDate ? new Date(values.startDate) : null}
                  onChange={(date: Date) => {
                    setFieldValue('startDate', date.toDateString());
                  }}
                  timescale={plannerModel?.time_properties?.time_granularity === 'Y' ? 'Y' : 'M'}
                />
              </Box>
            </FormControl>

            <Flex gap='12px'>
              <Button
                type='button'
                flex={1}
                h='48px'
                variant='outline'
                borderColor={BUILDER_MODE_V1_TOKENS.border}
                borderRadius='12px'
                fontSize='14px'
                fontWeight='500'
                color={BUILDER_MODE_V1_TOKENS.textDark}
                fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                onClick={() => {
                  resetForm();
                  onCancel();
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                flex={1}
                h='48px'
                bg={BUILDER_MODE_V1_TOKENS.primary}
                color='white'
                borderRadius='12px'
                fontSize='14px'
                fontWeight='500'
                fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                _hover={{ bg: BUILDER_MODE_V1_TOKENS.primary }}
                isLoading={isSubmitting}
              >
                Create scenario
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  );
});

AddScenarioFormV1.displayName = 'AddScenarioFormV1';

export default AddScenarioFormV1;
